import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import UserList from './UserList';
import UserForm from './UserForm';
import UserDetail from './UserDetail';
import logo from './reflection-logo.svg';
import { db } from './firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { AuthPage } from './AuthPage';
import { auth, signOut } from './auth';
import IssueRegistration from './IssueRegistration';
import IssueList from './IssueList';
import IssueForm from './IssueForm';
import IssueDetail from './IssueDetail';
import ProgressNotesPage from './ProgressNotesPage';
import AddNoteForm from './AddNoteForm';
import ContractorImportPage from './ContractorImportPage';
import SOPPage from './SOPPage';
import PrivacyPage from './PrivacyPage';
import ContactPage from './ContactPage';

function EditUserWrapper({ users, onSave, onCancel }) {
  const { id } = useParams();
  const idx = parseInt(id, 10);
  const editingUser = users[idx];
  return <UserForm onSave={onSave} editingUser={editingUser} onCancel={onCancel} />;
}

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [page, setPage] = useState(1);
  const usersPerPage = 25;
  const navigate = useNavigate();
  const [isSCUser, setIsSCUser] = useState(false);

  // Listen for real-time updates from Firestore
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'users'), (snapshot) => {
      const userList = [];
      snapshot.forEach(doc => userList.push({ ...doc.data(), id: doc.id }));
      // Sort users by creation time if available, otherwise by id
      userList.sort((a, b) => (a.createdAt || a.id).localeCompare(b.createdAt || b.id));
      setUsers(userList);
    });
    return () => unsub();
  }, []);

  // SC User check
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (user && user.email) {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        let found = false;
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.email && data.email.toLowerCase() === user.email.toLowerCase()) {
            found = true;
          }
        });
        setIsSCUser(found);
      } else {
        setIsSCUser(false);
      }
    });
    return () => unsubscribe();
  }, []);

  async function handleSave(user) {
    if (editingIdx !== null) {
      const userId = users[editingIdx].id;
      await updateDoc(doc(db, 'users', userId), user);
      setEditingIdx(null);
      navigate('/users', { replace: true });
    } else {
      // Add a createdAt timestamp for sorting and persistence
      await addDoc(collection(db, 'users'), { ...user, createdAt: new Date().toISOString() });
      navigate('/users', { replace: true });
    }
  }

  function handleEdit(idx, e) {
    if (!isSCUser) {
      if (e) e.preventDefault();
      alert('Only SC Users are allowed!');
      return;
    }
    setEditingIdx(idx);
    navigate('edit/' + idx);
  }

  async function handleDelete(idx, e) {
    if (!isSCUser) {
      if (e) e.preventDefault();
      alert('Only SC Users are allowed!');
      return;
    }
    const userId = users[idx].id;
    await deleteDoc(doc(db, 'users', userId));
  }

  function handleCancel() {
    setEditingIdx(null);
    navigate('/users');
  }

  // Paging logic
  const totalPages = Math.ceil(users.length / usersPerPage) || 1;
  const pagedUsers = users.slice((page - 1) * usersPerPage, page * usersPerPage);

  function handlePageChange(newPage) {
    setPage(newPage);
  }

  function handleRegisterClick(e) {
    if (!isSCUser) {
      e.preventDefault();
      alert('Only SC Users are allowed!');
      return;
    }
    navigate('register');
  }

  return (
    <>
      <Routes>
        <Route index element={
          <>
            <div className="users-header">
              <div></div>
              <button className="register-btn" onClick={handleRegisterClick}>Register New SC User</button>
            </div>
            <div className="table-container">
              <UserList 
                users={pagedUsers} 
                onEdit={handleEdit} 
                onDelete={handleDelete} 
                isSCUser={isSCUser}
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        } />
        <Route path="register" element={<UserForm onSave={handleSave} />} />
        <Route path="edit/:id" element={<EditUserWrapper users={users} onSave={handleSave} onCancel={handleCancel} />} />
        <Route path=":id" element={<UserDetail users={users} />} />
      </Routes>
    </>
  );
}

function IssuesPage() {
  const [issues, setIssues] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [page, setPage] = useState(1);
  const issuesPerPage = 25;
  const navigate = useNavigate();
  const [isSCUser, setIsSCUser] = useState(false);
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [hideClosed, setHideClosed] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  function handleExport() {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }
    const start = new Date(startDate); start.setHours(0,0,0,0);
    const end = new Date(endDate); end.setHours(23,59,59,999);

    const dataToExport = issues.filter(issue => {
      if (!issue.dateReported) return false;
      const d = new Date(issue.dateReported);
      return d >= start && d <= end;
    }).map(issue => ({
      id: issue.id,
      "Date Reported": issue.dateReported ? new Date(issue.dateReported) : '',
      "By Whom": issue.byWhom,
      "Description": issue.description,
      "Location": issue.location,
      "Compliance Risk": issue.complianceRisk,
      "Priority": issue.priority ? Number(issue.priority) : '',
      "Estimated Cost": issue.estimatedCost ? Number(issue.estimatedCost) : '',
      "Fund": issue.fund,
      "Target Date": issue.targetQtr ? new Date(issue.targetQtr) : '',
      "Status": issue.status
    }));

    if (dataToExport.length === 0) {
      alert("No issues found in the selected date range.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Issues");
    XLSX.writeFile(workbook, "Issues_Export.xlsx");
  }

  React.useEffect(() => {
    const unsub = onSnapshot(collection(db, 'issues'), (snapshot) => {
      const issueList = [];
      snapshot.forEach(doc => issueList.push({ ...doc.data(), id: doc.id }));
      issueList.sort((a, b) => (b.createdAt || b.id).localeCompare(a.createdAt || a.id));
      setIssues(issueList);
    });
    return () => unsub();
  }, []);

  // SC User check
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (user && user.email) {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        let found = false;
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.email && data.email.toLowerCase() === user.email.toLowerCase()) {
            found = true;
          }
        });
        setIsSCUser(found);
      } else {
        setIsSCUser(false);
      }
    });
    return () => unsubscribe();
  }, []);

  async function handleSave(issue) {
    if (editingIdx !== null) {
      const issueId = editingIdx.id;
      await updateDoc(doc(db, 'issues', issueId), issue);
      setEditingIdx(null);
      navigate('/issues', { replace: true });
    } else {
      await addDoc(collection(db, 'issues'), { ...issue, createdAt: new Date().toISOString() });
      navigate('/issues', { replace: true });
    }
  }

  function handleEdit(issue) {
    setEditingIdx(issue);
    navigate('edit/' + issues.findIndex(i => i.id === issue.id));
  }

  async function handleDelete(issue) {
    await deleteDoc(doc(db, 'issues', issue.id));
  }

  function handleCancel() {
    setEditingIdx(null);
    navigate('/issues');
  }

  // Filtering logic for total cost and paged issues
  let filteredIssues = hideClosed
    ? issues.filter(issue => (issue.status || '').toLowerCase() !== 'closed')
    : issues;

  if (searchTerm.trim() !== '') {
    const term = searchTerm.trim().toLowerCase();
    filteredIssues = filteredIssues.filter(issue =>
      Object.values(issue).some(val =>
        typeof val === 'string' && val.toLowerCase().includes(term)
      )
    );
  }

  if (priorityFilter.length > 0) {
    filteredIssues = filteredIssues.filter(issue => priorityFilter.includes(Number(issue.priority)));
  }

  // Reset page to 1 if current page is out of bounds after filtering
  const totalPages = Math.ceil(filteredIssues.length / issuesPerPage) || 1;
  if (page > totalPages) {
    setPage(1);
  }

  // Total cost: sum only filtered issues, and zero out cost for closed status
  const totalEstimatedCost = filteredIssues.reduce((sum, issue) => {
    if ((issue.status || '').toLowerCase() === 'closed') return sum;
    const val = parseFloat(issue.estimatedCost);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const pagedIssues = filteredIssues.slice((page - 1) * issuesPerPage, page * issuesPerPage);

  function handlePageChange(newPage) {
    setPage(newPage);
  }

  function handleRegisterNewIssueClick(e) {
    if (!isSCUser) {
      e.preventDefault();
      alert('Only SC Users are allowed!');
      return;
    }
    navigate('register');
  }

  return (
    <>
      <Routes>
        <Route index element={
          <>
            <div className="users-header">
              <div style={{display:'flex', alignItems:'center', gap:'2rem'}}>
                <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                  <label htmlFor="startDate">From:</label>
                  <input 
                    type="date" 
                    id="startDate"
                    value={startDate} 
                    onChange={e => setStartDate(e.target.value)}
                    style={{padding:'0.4rem', borderRadius:'4px', border:'1px solid #ccc'}}
                  />
                  <label htmlFor="endDate">To:</label>
                  <input 
                    type="date" 
                    id="endDate"
                    value={endDate} 
                    onChange={e => setEndDate(e.target.value)}
                    style={{padding:'0.4rem', borderRadius:'4px', border:'1px solid #ccc'}}
                  />
                  <button className="register-btn" onClick={handleExport} style={{backgroundColor:'#2e7d32'}}>
                    Export to Excel
                  </button>
                </div>
                <button className="register-btn" onClick={handleRegisterNewIssueClick}>Register New Issue</button>
              </div>
            </div>
            <div className="table-container full-width-table">
              <IssueList
                issues={pagedIssues}
                onEdit={handleEdit}
                onDelete={handleDelete}
                showHeading={true}
                totalEstimatedCost={totalEstimatedCost}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                priorityFilter={priorityFilter}
                setPriorityFilter={setPriorityFilter}
                hideClosed={hideClosed}
                setHideClosed={setHideClosed}
              />
              <div className="pagination">
                <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Prev</button>
                <span>Page {page} of {totalPages}</span>
                <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>Next</button>
              </div>
            </div>
          </>
        } />
        <Route path="register" element={<IssueForm onSave={handleSave} />} />
        <Route path="edit/:id" element={<EditIssueWrapper issues={issues} onSave={handleSave} onCancel={handleCancel} />} />
        <Route path=":id" element={<IssueDetail issues={issues} />} />
        <Route path=":issueId/progress" element={<ProgressNotesPage />} />
        <Route path=":issueId/progress/add" element={<AddNoteForm />} />
      </Routes>
    </>
  );
}

function EditIssueWrapper({ issues, onSave, onCancel }) {
  const { id } = useParams();
  const idx = parseInt(id, 10);
  const editingIssue = issues[idx];
  return <IssueForm onSave={onSave} editingIssue={editingIssue} onCancel={onCancel} />;
}

export default function App() {
  const [authed, setAuthed] = React.useState(false);
  const [isSCUser, setIsSCUser] = React.useState(false);

  React.useEffect(() => {
    // Only set authenticated if the user exists AND is verified
    const unsub = auth.onAuthStateChanged(async user => {
        if (user && user.emailVerified) {
            setAuthed(true);
            // Check if SC User
            const usersSnapshot = await getDocs(collection(db, 'users'));
            let found = false;
            usersSnapshot.forEach(doc => {
              const data = doc.data();
              if (data.email && data.email.toLowerCase() === user.email.toLowerCase()) {
                found = true;
              }
            });
            setIsSCUser(found);
        } else {
            setAuthed(false);
            setIsSCUser(false);
        }
    });
    return () => unsub();
  }, []);

  return (
    <Router>
      <nav className="navbar">
        <div className="nav-left">
          <Link to="/" style={{display: 'flex', alignItems: 'center', textDecoration: 'none'}} replace>
            <img src={logo} alt="Reflections Apartment Logo" className="logo" />
            <span className="company-name">Reflections&nbsp;Apartment<br />Strata Committee</span>
          </Link>
        </div>
        <div className="nav-right">
          {authed && isSCUser && <Link to="/users">SC Users</Link>}
          {authed && <Link to="/issues" style={{marginLeft: '2rem'}}>List of Issues</Link>}
          {authed && <Link to="/contractors/import" style={{marginLeft: '2rem'}}>Contractor List</Link>}
          {authed && <Link to="/sop" style={{marginLeft: '2rem'}}>SOP</Link>}
          {authed && <button className="register-btn" style={{marginLeft: '2rem', marginRight: '2rem'}} onClick={() => { signOut(auth); setAuthed(false); }}>Sign Out</button>}
        </div>
      </nav>
      <div style={{padding: '2rem'}}>
          <Routes>
            <Route path="*" element={
                !authed ? (
                    <AuthPage onAuth={() => setAuthed(true)} />
                ) : (
                    <AuthenticatedRoutes authed={authed} />
                )
            } />
          </Routes>
      </div>
    </Router>
  );
}

function AuthenticatedRoutes({ authed }) {
    if (!authed) return null;
    return (
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/users/*" element={<UsersPage />} />
            <Route path="/issues/*" element={<IssuesPage />} />
            <Route path="/contractors/import" element={<ContractorImportPage />} />
            <Route path="/sop" element={<SOPPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<HomePage />} />
        </Routes>
    );
}

function HomePage() {
  const navigate = useNavigate();
  return (
    <div className="home-container">
      <div className="home-title">Welcome to Reflections Apartment</div>
      <div className="home-desc">
        This web portal is to provide a centralized location to register, monitor, and traceability for all repair and maintenance issues.
      </div>
      
      <div style={{ marginTop: '2rem', marginBottom: '3rem', maxWidth: '800px' }}>
        <h3 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Notes on using the portal:</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ccc' }}>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Search</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Use the priority checkboxes or keyword to filter</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Priority 1</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Owner-reported issues</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Priority 2</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Common area issues</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Priority 3</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Building appearance improvements</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Priority 4</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Capital works planning</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Priority 5</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Issues on hold or under monitoring</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', flexDirection: 'row', gap: '4rem', alignItems: 'center', marginTop: '1rem' }}>
        <button className="home-btn" onClick={() => navigate('/contact')} style={{ fontWeight: 'bold', fontSize: '1.1rem', textDecoration: 'underline', background: 'none', color: 'black', border: 'none', cursor: 'pointer' }}>Contact Us</button>
        <button className="home-btn" onClick={() => navigate('/privacy')} style={{ fontWeight: 'bold', fontSize: '1.1rem', textDecoration: 'underline', background: 'none', color: 'black', border: 'none', cursor: 'pointer' }}>Privacy Statement</button>
      </div>
    </div>
  );
}